/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * **One value line, at every depth** — `X38` (#1229), the turn-2 half of `XS3`.
 *
 * > *The row is its depth-zero rendering, the editor is the same control with room for a value that
 * > has structure, and there is no second authoring model anywhere in the product.*
 *
 * `X6` says the mapping control is a **view of the expression model**, which is a statement two
 * components can satisfy by agreeing. This is the statement one component satisfies by construction,
 * and the measured cost of the agreeing version was five duplications on one screen: the expression
 * editor drew its own Context Path, its own Transform, its own Default, its own extract-with-regex
 * checkbox and its own Description **beneath a row that already had all five**. Todd's report of
 * *"transform in the `then` line and below it, as well as an extract-with-regex checkbox and an
 * extract pill"* is the same defect seen from a branch.
 *
 * So a value — the row's, the editor's own, or a conditional branch's — is this: the field, the
 * Default that shares its line, and the capability chips beneath it. Nothing else authors a path, a
 * transform, a regex or a default anywhere in this control.
 *
 * **What it deliberately does not render** is the structured editor for a shape the line cannot show.
 * A `conditional` or a `computed` reads back here as a summary and is edited *below* this component,
 * which is exactly what `MappingControl` does at depth zero — the recursion is in the caller, so a
 * branch that widens to hold a nested shape (#542, `X42`) becomes an addition rather than a rewrite.
 */

import React from 'react';
import { Box, HStack, VStack } from '@chakra-ui/react';
import { ValueLine } from './ValueLine';
import { CapabilityChipRow, type CapabilitySlot } from './CapabilityChipRow';
import { ExpressionDefaultControl } from './mapping-knobs';
import { chipCapabilitiesFor } from './value-line';
import type { MappingLeaf } from './ortb-body-editor.adapters';
import type { CapabilityRefused } from './MappingControl';
import type { MacroExpression } from '../../../types/decorator-expression';
import type { MappingSuggestion } from '../inputs/MappingAutocompleteInput';
import type { SharedExpressionSlot } from './useSharedExpressionSlot';

/**
 * The expression as the leaf the value line edits.
 *
 * A `static` expression becomes a **static leaf** rather than an expression one wherever it can:
 * that is the leaf model's canonical spelling of a literal, and it is what keeps the line's own
 * classifier from having to know about two of them. It stays an expression leaf when it carries a
 * note, because a static leaf has nowhere to keep one.
 */
export function expressionToLeaf(expression: MacroExpression): MappingLeaf {
  if (expression.type === 'static' && !expression.description) {
    return { path: [], kind: 'static', staticValue: expression.value };
  }
  return { path: [], kind: 'expression', expression };
}

/**
 * **A leaf the value line produced, as the expression this control holds.**
 *
 * The bridge is three shapes wide and no wider, because only three are what a value line can
 * produce: a literal is a `static`, a path is a `calculated`, and a `{macro}` is a `macro` node
 * (`X58` — until then an expression had no macro form, and the line refused one). The structured
 * shapes are the chips'.
 *
 * The existing expression is passed so a path→path edit **keeps its parts**: the regex and the
 * transform belong to the derived value, and retyping the path is not a decision to discard them.
 * That is the same rule `textToLeaf` follows one layer down, said once here rather than inferred.
 */
export function leafToExpression(leaf: MappingLeaf, previous: MacroExpression): MacroExpression {
  if (leaf.kind === 'expression' && leaf.expression) {
    if (leaf.expression.type === 'calculated') {
      const kept = previous.type === 'calculated' ? previous : undefined;
      return { ...kept, ...leaf.expression, type: 'calculated' };
    }
    return leaf.expression;
  }
  const value = leaf.kind === 'static' && leaf.staticValue !== undefined ? String(leaf.staticValue) : '';
  const note = previous.type === 'static' ? previous.description : undefined;
  return note ? { type: 'static', value, description: note } : { type: 'static', value };
}

export interface ExpressionValueLineProps {
  expression: MacroExpression;
  onChange: (next: MacroExpression) => void;
  /** Rendered at the head of the line — a branch's `Then` / `Else`. `null` where the line has no name. */
  leading: React.ReactNode | null;
  /** Path autocomplete for this surface. `null` means *use the editor's default schema*. */
  pathSuggestions: ((query: string) => MappingSuggestion[] | Promise<MappingSuggestion[]>) | null;
  /**
   * Why a chip cannot act here, per slot — the caller's answer, merged with the one this component
   * always knows: whether the value has anything for Extract and Transform to compose onto.
   * `chipCapabilitiesFor` owns that sentence, so a chip refused on the row and the same chip refused
   * inside the editor cannot come to say different things (`#1187`).
   */
  refuses: Partial<Record<CapabilitySlot, CapabilityRefused>>;
  /** Hand the value to a bigger shape. Required and nullable — a line with no way up has to say so. */
  onOpenInEditor: ((slot: CapabilitySlot) => void) | null;
  /** Which chip the surface below this line belongs to, or `null`. */
  openSlot: CapabilitySlot | null;
  /**
   * **Does a value in this slot answer *what if it does not resolve*?** (`X53` sub-question 5,
   * `#1299`, ratified by Todd 2026-09-10.)
   *
   * The slot is the second half of the question. Which *shapes* carry a `default` is
   * `declaresDefaultSlot` and is unchanged; whether **this position** is somewhere that answer
   * belongs is the caller's, and until now nobody was asked — so every operand of every comparison
   * and every calculation offered one.
   *
   * > *"`Value to test` should not have a default. if the user is unsure the value will resolve,
   * > that's an `exist` test… and the 'otherwise' represents the 'default' for a condition anyway.
   * > For calculations, the one place default makes sense is at the result of a computation scope…
   * > not per operand."*
   *
   * Measured before it was removed rather than after: a default on a condition operand makes an
   * **absent** value satisfy `eq`, while `exists` on the same path says it is not there — two
   * operators disagreeing about whether a value is present, decided by a control that reads as a
   * convenience.
   *
   * Required rather than optional, and that is the whole point (`#1134`): an optional prop here
   * renders nothing when forgotten, and *nothing* is exactly what a slot that should offer a default
   * would then show.
   */
  offersDefault: boolean;
  /**
   * **What the empty box says** — `null` for the line's own wording (`#1384`).
   *
   * A condition's *Compare with* is the slot that needs it: an operand that was never set and an
   * empty literal render the same empty box, and the engine answers them differently — unset is a
   * side that does not resolve, so `eq` never holds, where `''` is a value. The placeholder is the
   * only thing on screen that can tell them apart. Required and nullable, so a slot with the same
   * ambiguity has to say whether it has it (`#1134`).
   */
  placeholder: string | null;
  /**
   * **May the value in this slot be chosen by a condition?** (`X53` sub-question 1, `#1284`.)
   *
   * The last of the five, and it is **one boolean rather than the shape set the issue assumed** —
   * which is the measurement, not a shortcut. Three slots render a value line, and across the six
   * members of `MacroExpression` they differ on exactly two:
   *
   * | | `static` | `calculated` | `computed` | `conditional` | `suppress` | `reference` |
   * | --- | --- | --- | --- | --- | --- | --- |
   * | the editor's own line | ✓ | ✓ | ✓ | ✓ | ✓ | ✓ |
   * | a branch outcome | ✓ | ✓ | ✓ | ✓ *(bounded)* | ✓ | ✓ |
   * | an operand | ✓ | ✓ | ✓ | **✗** | **✗** | ✓ |
   *
   * And only `conditional` has a **control** — the `If…` chip. A `suppress` is authored by the
   * branch's own *never send it* select, which only a branch has, so nothing can put one in a slot
   * that would refuse it. Declaring it would be a member nothing reads: *not a licence to generalize
   * without a purpose.*
   *
   * **Required, which is the point** (`#1134`). It produced the audit above rather than requiring
   * one, and the operand's answer had been a **silent drop** — `if (next.type !== 'suppress' &&
   * next.type !== 'conditional') onChange(next)` — a guard no control could reach and no reader
   * could see.
   *
   * A slot that accepts one may still refuse it for its own reason: a branch accepts a decision and
   * bounds the nesting (`E4`), and that refusal is the caller's and wins over this one.
   */
  acceptsConditional: boolean;
  /**
   * **The `Shared…` slot's state, owned by the caller** (`X30c`, `#1303`).
   *
   * Every caller used to answer this chip itself, and each answered the older question — *open the
   * picker* — so a shared expression inside a conditional or a calculation could be picked and then
   * never opened, edited or replaced. Reported by Todd 2026-09-10: *"shared expressions within
   * another expression don't have the open/edit/replace capability."*
   *
   * The three acts are `X30a`'s and are the same at every depth, which is why the state machine is
   * {@link useSharedExpressionSlot} and not three copies. It is held by the caller because the
   * picker must be *the only thing* under the chip row while it is open, and only the caller can
   * suppress the form it draws below.
   *
   * Required rather than optional: forgotten, the chip would open **nothing**, which is exactly the
   * state this is fixing.
   */
  sharedSlot: SharedExpressionSlot;
}

/**
 * **Why a value here cannot be chosen by a condition** — one sentence, so a slot that refuses it and
 * a slot that refuses it cannot come to say different things (`#1187`: shared logic is not shared
 * words, and a duplicated explanation is the copy nothing checks).
 *
 * It says what an operand **does** take before it says what it does not, because a refusal is read
 * by whoever is being turned away and *you cannot* on its own leaves them nowhere.
 */
const SLOT_TAKES_NO_DECISION: CapabilityRefused = {
  reason: 'This takes a value, a path, a calculation or a shared expression — not a condition.',
};

export const ExpressionValueLine: React.FC<ExpressionValueLineProps> = ({
  expression,
  onChange,
  leading,
  pathSuggestions,
  refuses,
  onOpenInEditor,
  openSlot,
  offersDefault,
  placeholder,
  acceptsConditional,
  sharedSlot,
}) => {
  const { basePath, refusesComposition } = chipCapabilitiesFor(expression);
  /**
   * **The `Shared…` slot belongs to the value, not to whatever is rendering it** (`X30c`, `#1303`).
   *
   * Every caller used to answer this chip itself, and each answered the older question — *open the
   * picker* — so a shared expression inside a conditional or a calculation could be picked and then
   * never opened, edited or replaced. Reported by Todd 2026-09-10.
   *
   * The three acts are `X30a`'s and are the same at every depth; what a caller still owns is the two
   * errands that reshape (`if`, `calculate`), which genuinely differ — a row hands its value up, a
   * branch reshapes in place.
   */
  /* A surface that refuses the chip keeps its refusal: `CapabilityChipRow` disables it, so nothing
     here can open. The editor's own line is that case — replacing the expression you are editing
     with a pointer to a different one is not the same act as answering a branch with one. */
  const refusesShared = !!refuses.shared;
  return (
    <VStack align="stretch" gap={0}>
      <HStack gap={2} align="center">
        {leading}
        <Box flex="1" minWidth={0}>
          <ValueLine
            leaf={expressionToLeaf(expression)}
            onChange={(leaf) => onChange(leafToExpression(leaf, expression))}
            pathSuggestions={pathSuggestions}
            placeholder={placeholder}
            acceptsMacro
            macroRefusal={null}
            derivedRefusal={null}
            // A value inside an expression is text to the engine (`StaticExpression.value`); the
            // destination's type is applied to what the expression resolves to, by `$valueType`.
            literalType={null}
            /* The Default shares the line, because *read this, send that if it is not there* is one
               answer rather than two settings (#1128) — the same placement the row uses. And it is
               drawn only where the **slot** is somewhere that answer belongs, which is `offersDefault`. */
            trailing={offersDefault ? <ExpressionDefaultControl expression={expression} onChange={onChange} /> : null}
          />
        </Box>
      </HStack>
      <CapabilityChipRow
        expression={expression}
        onChange={onChange}
        basePath={basePath}
        refuses={{
          ...(refusesComposition ? { extract: refusesComposition, transform: refusesComposition } : {}),
          /* **The declared refusal, and the caller's wins over it.** A slot that takes no decision
             says so once here; a slot that takes one and bounds the nesting has its own sentence,
             which is a different claim about the same chip (`E4`). */
          ...(acceptsConditional ? {} : { if: SLOT_TAKES_NO_DECISION }),
          ...refuses,
        }}
        onOpenInEditor={
          onOpenInEditor || !refusesShared
            ? (slot) => {
                if (slot === 'shared' && !refusesShared) sharedSlot.activate();
                else onOpenInEditor?.(slot);
              }
            : null
        }
        /* The caller's answer wins while it has one — the editor below a row belongs to the shape —
           and the shared slot's own surfaces light the chip when they are what is open. */
        openSlot={openSlot ?? sharedSlot.openSlot}
      />
    </VStack>
  );
};
